import React from "react"
import { Link } from "react-router-dom"
import { PlayCircle, PauseCircle } from "react-bootstrap-icons"
import { useDispatch, useSelector } from "react-redux"
import { prependAndPlay, playOrPause } from "../../stores/mediaPlayerSlice"
import { usePost } from "../../hooks/usePost"
import { sortedSongs } from "./PlaylistListItem"
import { PLAYLIST_PLACEHOLDER_IMAGE_URL } from "../../util/constants"
import { formatPlaylistDuration } from "../../util/dates"

const styles = {
  title: "text-base md:text-2xl font-bold text-black-800 leading-none pb-1",
  byline: "text-gray-500 leading-none text-xs pb-1",
  count: "text-xs md:text-sm text-black-800 leading-none md:leading-normal",
}

const ICON_SIZE = "2.5rem"

interface PlaylistListItemShortProps {
  playlist: any
}

const PlaylistListItemShort: React.FC<PlaylistListItemShortProps> = ({ playlist }) => {
  const dispatch = useDispatch<any>()
  const currentMediaItem = useSelector((state: any) => state.mediaPlayer.value.currentMediaItem)
  const playerState = useSelector((state: any) => state.mediaPlayer.value.playerState)

  const postId = playlist.post_id || playlist.post?.id
  const { data: postData } = usePost(postId, "songs")
  const playlistDetails = (postData as any)?.post

  const isCurrentlyPlaying = playerState === "playing" && currentMediaItem?.playlistId === playlist.id
  const isCurrentlyPaused = playerState === "paused" && currentMediaItem?.playlistId === playlist.id

  const handlePlay = (e: React.MouseEvent) => {
    e.preventDefault()
    if (isCurrentlyPlaying || isCurrentlyPaused) {
      dispatch(playOrPause())
    } else {
      const songs = sortedSongs(playlistDetails, { ...playlist, nada_path: playlist.nada_path })
      if (songs.length > 0) dispatch(prependAndPlay(songs))
    }
  }

  const coverImage =
    playlist.post?.external_photo_src ||
    playlist.post?.cover_image_url_small ||
    playlist.post?.cover_image_url ||
    PLAYLIST_PLACEHOLDER_IMAGE_URL

  const songCount = playlistDetails?.songs?.length ?? playlist.playlist_posts_count

  return (
    <li
      className="flex items-start gap-4 py-3 border-b border-gray-100 last:border-0"
      style={{ marginBottom: "0.75rem" }}
    >
      <Link to={playlist.nada_path} className="flex-shrink-0">
        <img
          src={coverImage}
          alt={playlist.name}
          className="rounded object-cover"
          style={{ width: 96, height: 96 }}
        />
      </Link>
      <Link to={playlist.nada_path} className="flex-1 min-w-0">
        <div className={styles.title}>{playlist.name}</div>
        {playlist.user_nickname && (
          <div className={styles.byline}>by {playlist.user_nickname}</div>
        )}
        {songCount != null && (
          <div className={styles.count}>
            {songCount} songs
            {playlistDetails?.songs && (() => {
              const dur = formatPlaylistDuration(playlistDetails.songs)
              return dur ? `, ${dur}` : null
            })()}
          </div>
        )}
      </Link>
      <button
        type="button"
        onClick={handlePlay}
        className="flex-shrink-0 text-green-700 hover:text-green-900 focus:outline-none"
        aria-label={isCurrentlyPlaying ? "Pause" : "Play"}
      >
        {isCurrentlyPlaying ? (
          <PauseCircle size={ICON_SIZE} />
        ) : (
          <PlayCircle size={ICON_SIZE} />
        )}
      </button>
    </li>
  )
}

export default PlaylistListItemShort
